/**
 * GoogleReviewCard - Professional Google Reviews widget with official branding
 * Displays rating, review count, and links to leave a review
 * Stats are fetched from the backend API and can be updated in Admin
 */
import { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import api from '../utils/apiClient';

// Default values (used while loading or if API fails)
const DEFAULT_STATS = {
  rating: 4.9,
  total_reviews: 150,
  distribution: { 5: 85, 4: 10, 3: 3, 2: 1, 1: 1 },
  google_review_url: "https://g.page/r/CbYHsfEsqm9iEAI/review",
  // Correct Google Maps URL for Bulaccino Cafe Namaka, Nadi, Fiji
  google_maps_url: "https://www.google.com/maps/search/Bulaccino+Cafe+Namaka+Nadi+Fiji"
};

// Hook to fetch review stats
function useReviewStats() {
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/google-reviews/stats');
        setStats({
          rating: res.data.rating,
          total_reviews: res.data.total_reviews,
          distribution: res.data.distribution,
          google_review_url: res.data.google_review_url,
          google_maps_url: res.data.google_maps_url
        });
      } catch (err) {
        console.error('Failed to fetch review stats:', err);
      }
      setLoading(false);
    };
    fetchStats();
  }, []);

  return { stats, loading };
}

// Google "G" Logo SVG
function GoogleLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// Star rating display
function StarRating({ rating, size = "default" }) {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  const starSize = size === "small" ? "w-4 h-4" : "w-5 h-5";
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <Star 
          key={i} 
          className={`${starSize} text-amber-400 fill-amber-400`} 
        />
      );
    } else if (i === fullStars && hasHalfStar) {
      stars.push(
        <div key={i} className="relative">
          <Star className={`${starSize} text-gray-300`} />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className={`${starSize} text-amber-400 fill-amber-400`} />
          </div>
        </div>
      );
    } else {
      stars.push(
        <Star 
          key={i} 
          className={`${starSize} text-gray-300 dark:text-gray-600`} 
        />
      );
    }
  }
  
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

// Compact version for footer
export function GoogleReviewCardCompact({ compact = false }) {
  const { stats } = useReviewStats();
  
  if (compact) {
    // Extra compact version for equal-width footer items
    return (
      <a
        href={stats.google_review_url}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary/50 transition-all cursor-pointer w-full h-full"
        data-testid="google-review-card"
      >
        <GoogleLogo className="w-5 h-5 flex-shrink-0" />
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="text-lg font-bold text-foreground">{stats.rating}</span>
          <StarRating rating={stats.rating} size="small" />
        </div>
        <span className="text-xs font-medium text-primary group-hover:underline whitespace-nowrap">
          Review →
        </span>
      </a>
    );
  }
  
  return (
    <a
      href={stats.google_review_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 px-5 py-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
      data-testid="google-review-card"
    >
      {/* Google Logo */}
      <div className="flex-shrink-0">
        <GoogleLogo className="w-10 h-10" />
      </div>
      
      {/* Rating Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl font-bold text-foreground">{stats.rating}</span>
          <StarRating rating={stats.rating} size="small" />
        </div>
        <p className="text-xs text-muted-foreground">
          Based on {stats.total_reviews}+ Google reviews
        </p>
      </div>
      
      {/* CTA */}
      <div className="flex-shrink-0">
        <span className="text-xs font-medium text-primary group-hover:underline whitespace-nowrap">
          Write a review
        </span>
      </div>
    </a>
  );
}

// Full card version (for other placements)
export function GoogleReviewCardFull({ className = "" }) {
  const { stats } = useReviewStats();
  
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <GoogleLogo className="w-8 h-8" />
        <div>
          <h3 className="font-semibold text-foreground">Google Reviews</h3>
          <p className="text-xs text-muted-foreground">Bulaccino Cafe</p>
        </div>
      </div>
      
      {/* Rating Display */}
      <div className="flex items-center gap-4 mb-4">
        <span className="text-5xl font-bold text-foreground">{stats.rating}</span>
        <div>
          <StarRating rating={stats.rating} />
          <p className="text-sm text-muted-foreground mt-1">
            {stats.total_reviews}+ reviews
          </p>
        </div>
      </div>
      
      {/* Rating Distribution Bars */}
      <div className="space-y-1.5 mb-5">
        {[5, 4, 3, 2, 1].map((stars) => (
          <div key={stars} className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-3">{stars}</span>
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-400 rounded-full transition-all"
                style={{ width: `${stats.distribution[stars] || 0}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground w-8 text-right">
              {stats.distribution[stars] || 0}%
            </span>
          </div>
        ))}
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-3">
        <a
          href={stats.google_review_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-medium text-center hover:bg-primary/90 transition-colors"
          data-testid="google-review-btn"
        >
          Write a Review
        </a>
        <a
          href={stats.google_maps_url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-foreground rounded-full text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          See All
        </a>
      </div>
      
      {/* Google Attribution */}
      <p className="text-[10px] text-muted-foreground text-center mt-4">
        Reviews from Google Maps
      </p>
    </div>
  );
}

// Badge version (minimal)
export function GoogleReviewBadge() {
  const { stats } = useReviewStats();
  
  return (
    <a
      href={stats.google_review_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
      data-testid="google-review-badge"
    >
      <GoogleLogo className="w-5 h-5" />
      <div className="flex items-center gap-1">
        <span className="font-bold text-foreground">{stats.rating}</span>
        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
      </div>
      <span className="text-xs text-muted-foreground">
        ({stats.total_reviews}+)
      </span>
    </a>
  );
}

// Export the hook for custom implementations
export { useReviewStats };

export default GoogleReviewCardCompact;
